// IndexedDB cache of the account's data (activities, samples, custom workouts, ghosts, replays, collection), local
// creations and card photos. In-memory fallback if unavailable. Account stores are emptied at sign-out (ADR 0013).
const NAME = 'gruvetto', VERSION = 5;
const STORES = { activities: 'id', samples: 'id', workouts: 'id', ghosts: 'key', creations: 'id', photos: 'key', replays: 'id', collection: 'id' };

export type StoreName = keyof typeof STORES;
/** Stores holding the signed-in account's data: emptied at sign-out so the next player sees nothing of the previous one. */
export const ACCOUNT_STORES: StoreName[] = ['activities', 'samples', 'workouts', 'ghosts', 'replays', 'collection'];
/** A stored record; callers narrow it with the generic parameter of `all` / `get`. */
export interface DbRow { id?: string; key?: string; [field: string]: unknown }
type Memory = Map<string, DbRow>;

let dbp: Promise<IDBDatabase | null> | null = null;
const memory = Object.fromEntries(Object.keys(STORES).map(k => [k, new Map() as Memory])) as Record<StoreName, Memory>;

function open(): Promise<IDBDatabase | null> {
  if (dbp) return dbp;
  dbp = new Promise<IDBDatabase | null>(resolve => {
    if (typeof indexedDB === 'undefined') return resolve(null);
    const req = indexedDB.open(NAME, VERSION);
    req.onupgradeneeded = () => {
      const d = req.result;
      for (const [s, key] of Object.entries(STORES)) if (!d.objectStoreNames.contains(s)) d.createObjectStore(s, { keyPath: key });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => { console.warn('IndexedDB unavailable, using in-memory storage', req.error); resolve(null); };
  });
  return dbp;
}

async function tx<T>(store: StoreName, mode: IDBTransactionMode, fn: (os: IDBObjectStore | null, mem: Memory) => T | Promise<T>): Promise<T> {
  const d = await open();
  if (!d) return fn(null, memory[store]);
  return new Promise<T>((resolve, reject) => {
    const t = d.transaction(store, mode), os = t.objectStore(store);
    let out: T;
    Promise.resolve(fn(os, memory[store])).then(v => { out = v; });
    t.oncomplete = () => resolve(out);
    t.onerror = () => reject(t.error);
  });
}
const req2p = <T>(r: IDBRequest<T>): Promise<T> => new Promise((res, rej) => { r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error); });

export const db = {
  all: <T = DbRow>(store: StoreName) => tx(store, 'readonly', (os, mem) => (os ? req2p(os.getAll() as IDBRequest<T[]>) : ([...mem.values()] as T[]))),
  get: <T = DbRow>(store: StoreName, key: string) => tx(store, 'readonly', (os, mem) => (os ? req2p(os.get(key) as IDBRequest<T | undefined>) : (mem.get(key) as T | undefined))),
  put: (store: StoreName, val: object) => tx<unknown>(store, 'readwrite', (os, mem) => (os ? req2p(os.put(val)) : mem.set((val as DbRow)[STORES[store]] as string, val as DbRow))),
  clear: (store: StoreName) => tx<unknown>(store, 'readwrite', (os, mem) => (os ? req2p(os.clear()) : mem.clear())),
  del: (store: StoreName, key: string) => tx<unknown>(store, 'readwrite', (os, mem) => (os ? req2p(os.delete(key)) : mem.delete(key))),
};
